import { Link, useParams, Navigate } from 'react-router-dom';
import { getPostBySlug, blogPosts } from '../../data/blog';
import { WHATSAPP_CTA_URL } from '../../data/whatsapp';
import SEOMeta from '../../components/SEOMeta';
import { useDarkMode } from '../../hooks/useDarkMode';

function inlineBold(text: string, baseKey: number): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${baseKey}-${idx}`} style={{ fontWeight: 600, color: 'var(--heading-ink)' }}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

function renderContent(content: string) {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let key = 0;
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // H2
    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={key++} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--heading-ink)', marginTop: 36, marginBottom: 12 }}>
          {line.replace('## ', '')}
        </h2>
      );
      i++; continue;
    }

    // H3
    if (line.startsWith('### ')) {
      elements.push(
        <h3 key={key++} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 18, fontWeight: 700, color: 'var(--amber-deep)', marginTop: 28, marginBottom: 8 }}>
          {line.replace('### ', '')}
        </h3>
      );
      i++; continue;
    }

    // Horizontal rule
    if (line.trim() === '---') {
      elements.push(<hr key={key++} style={{ border: 'none', height: 1, background: 'linear-gradient(90deg, transparent, var(--border-fade), transparent)', margin: '32px 0' }} />);
      i++; continue;
    }

    // Markdown table — collect consecutive | lines
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      // Filter out separator rows (|---|---|)
      const dataRows = tableLines.filter((l) => !/^\s*\|[\s\-|:]+\|\s*$/.test(l));
      const parseCells = (l: string) => l.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
      const [headerRow, ...bodyRows] = dataRows;
      const headers = parseCells(headerRow);
      elements.push(
        <div key={key++} style={{ overflowX: 'auto', margin: '28px 0', borderRadius: 10, border: '1px solid var(--border-soft)', boxShadow: '0 2px 12px rgba(74,55,40,0.06)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--earth)' }}>
                {headers.map((h, idx) => (
                  <th key={idx} style={{ padding: '12px 16px', textAlign: 'left', color: 'var(--gold-pale)', fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: 12, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, rIdx) => {
                const cells = parseCells(row);
                const isLast = cells[cells.length - 1];
                return (
                  <tr key={rIdx} style={{ background: rIdx % 2 === 0 ? 'var(--warm-white)' : 'var(--gold-pale)' }}>
                    {cells.map((cell, cIdx) => (
                      <td
                        key={cIdx}
                        style={{
                          padding: '11px 16px',
                          borderBottom: '1px solid var(--border-soft)',
                          verticalAlign: 'top',
                          lineHeight: 1.5,
                          fontFamily: "'DM Sans', sans-serif",
                          color: cIdx === cells.length - 1 ? 'var(--amber-deep)' : 'var(--text-body)',
                          fontWeight: cIdx === 0 ? 500 : (cIdx === cells.length - 1 ? 500 : 300),
                          background: cIdx === cells.length - 1 && isLast ? 'rgba(200,131,42,0.08)' : undefined,
                        }}
                      >
                        {inlineBold(cell, key * 100 + rIdx * 10 + cIdx)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Bullet list — collect consecutive - lines
    if (line.trimStart().startsWith('- ')) {
      const listItems: string[] = [];
      while (i < lines.length && lines[i].trimStart().startsWith('- ')) {
        listItems.push(lines[i].trimStart().replace(/^- /, ''));
        i++;
      }
      elements.push(
        <ul key={key++} style={{ margin: '8px 0 16px 0', paddingLeft: 20 }}>
          {listItems.map((item, idx) => (
            <li key={idx} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 300, color: 'var(--text-body)', lineHeight: 1.8, marginBottom: 4 }}>
              {inlineBold(item, key * 100 + idx)}
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      elements.push(<div key={key++} style={{ height: 8 }} />);
      i++; continue;
    }

    // Regular paragraph
    elements.push(
      <p key={key++} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 300, color: 'var(--text-body)', lineHeight: 1.85, marginBottom: 12 }}>
        {inlineBold(line, key)}
      </p>
    );
    i++;
  }

  return elements;
}

export default function PostPage() {
  const isDark = useDarkMode();
  const { slug } = useParams<{ slug: string }>();
  const post = getPostBySlug(slug ?? '');

  if (!post) return <Navigate to="/blog" replace />;

  const relatedPosts = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <>
      <SEOMeta
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        ogType="article"
      />
    <div style={{ background: 'var(--cream)', minHeight: '100vh' }}>
      {/* Header */}
      <header style={{ background: 'var(--warm-white)', borderBottom: '1px solid var(--beige-mid)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-24 flex items-center justify-between">
          <Link to="/">
            <img src={isDark ? '/logo-dark.png' : '/logo-light.png'} alt="Inspira Músicas" className="w-auto object-contain" style={{ height: 52 }} />
          </Link>
          <a
            href={WHATSAPP_CTA_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200"
            style={{
              background: 'var(--gold)',
              color: '#fff',
              fontFamily: "'DM Sans', sans-serif",
              boxShadow: '0 2px 12px rgba(200,131,42,0.25)',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gold-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--gold)'; }}
          >
            Criar Minha Música
          </a>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-3xl mx-auto px-4 md:px-8 pt-8 pb-2">
        <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif" }}>
          <Link to="/blog" style={{ color: 'var(--gold)', textDecoration: 'none' }}>
            Blog
          </Link>
          <span>›</span>
          <span
            style={{
              maxWidth: 240,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'inline-block',
            }}
          >
            {post.title}
          </span>
        </div>
      </div>

      {/* Article */}
      <article className="max-w-3xl mx-auto px-4 md:px-8 pb-16">
        {/* Cover */}
        <div
          className="w-full rounded-2xl flex items-center justify-center mb-8 mt-4"
          style={{ height: 260, background: post.coverColor, fontSize: 80 }}
        >
          {post.coverEmoji}
        </div>

        {/* Meta */}
        <div className="flex items-center gap-4 mb-4 flex-wrap">
          <span
            className="text-xs font-bold uppercase tracking-widest"
            style={{ color: 'var(--gold)', fontFamily: "'DM Sans', sans-serif" }}
          >
            {post.category}
          </span>
          <span className="text-xs" style={{ color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif" }}>
            {post.date}
          </span>
          <span className="text-xs" style={{ color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif" }}>
            {post.readTime} min de leitura
          </span>
        </div>

        {/* Title */}
        <h1
          className="font-bold mb-6 leading-tight"
          style={{
            fontSize: 'clamp(26px, 4vw, 36px)',
            color: 'var(--heading-ink)',
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 800,
            letterSpacing: '-0.03em',
          }}
        >
          {post.title}
        </h1>

        {/* Excerpt */}
        <p
          className="text-lg mb-8 pb-8"
          style={{
            color: 'var(--text-strong)',
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 400,
            lineHeight: 1.75,
            borderBottom: '1px solid var(--border-soft)',
          }}
        >
          {post.excerpt}
        </p>

        {/* Body */}
        <div>{renderContent(post.content)}</div>

        {/* Inline CTA */}
        <div
          className="mt-12 rounded-2xl p-8 text-center"
          style={{ background: 'var(--warm-white)', border: '1px solid var(--border-soft)' }}
        >
          <p
            className="text-xl font-bold mb-2"
            style={{ color: 'var(--heading-ink)', fontFamily: "'DM Sans', sans-serif", fontWeight: 800, letterSpacing: '-0.03em' }}
          >
            Pronto para criar uma música única?
          </p>
          <p
            className="text-sm mb-5"
            style={{ color: 'var(--text-muted)', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
          >
            Entrega em até 30 minutos. Garantia total ou dinheiro de volta.
          </p>
          <a
            href={WHATSAPP_CTA_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-7 py-3 rounded-full font-semibold text-sm transition-all duration-200"
            style={{
              background: 'var(--gold)',
              color: '#fff',
              fontFamily: "'DM Sans', sans-serif",
              boxShadow: '0 4px 20px rgba(200,131,42,0.30)',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--gold-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--gold)';
            }}
          >
            Criar Minha Música Agora
          </a>
        </div>
      </article>

      {/* Related posts */}
      {relatedPosts.length > 0 && (
        <section style={{ background: 'var(--warm-white)', borderTop: '1px solid var(--beige-mid)' }}>
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-14">
            <h3
              className="text-2xl font-bold mb-8"
              style={{ color: 'var(--heading-ink)', fontFamily: "'DM Sans', sans-serif", fontWeight: 800, letterSpacing: '-0.03em' }}
            >
              Continue lendo
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((related) => (
                <Link
                  key={related.slug}
                  to={`/blog/${related.slug}`}
                  className="block rounded-2xl overflow-hidden transition-all duration-300"
                  style={{
                    background: 'var(--cream)',
                    border: '1px solid var(--border-soft)',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 8px 32px rgba(200,131,42,0.12)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div
                    className="flex items-center justify-center"
                    style={{ height: 120, background: related.coverColor, fontSize: 48 }}
                  >
                    {related.coverEmoji}
                  </div>
                  <div style={{ padding: '18px 20px 20px' }}>
                    <span
                      className="text-xs font-bold uppercase tracking-widest block mb-1.5"
                      style={{ color: 'var(--gold)', fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {related.category}
                    </span>
                    <p
                      className="font-bold leading-snug"
                      style={{ fontSize: 15, color: 'var(--heading-ink)', fontFamily: "'DM Sans', sans-serif", fontWeight: 700 }}
                    >
                      {related.title}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
    </>
  );
}
